import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { purchaseOrdersApi } from './api';
import type {
  AmendPOPayload,
  ApprovePOPayload,
  AutoGeneratePOPayload,
  CancelPOPayload,
  ReverseReceiptPayload,
  CreatePOPayload,
  NotifyPOPayload,
  POListParams,
  POListResponse,
  POStats,
  POStatsParams,
  PurchaseOrder,
  ReceivePOPayload,
  RejectPOPayload,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function usePurchaseOrders(params: POListParams): UseQueryResult<POListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'purchase-orders',
      tenantId,
      params.page ?? 1,
      params.status ?? '',
      params.search ?? '',
      params.start_date ?? '',
      params.end_date ?? '',
      params.location_id ?? '',
      params.supplier_name ?? '',
      params.mobile ?? '',
      params.po_number ?? '',
    ],
    queryFn: () => purchaseOrdersApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// Keyed under ['purchase-orders', tenantId, ...] so the existing mutation
// invalidations refresh the KPI row along with the list.
export function usePurchaseOrderStats(params: POStatsParams): UseQueryResult<POStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['purchase-orders', tenantId, 'stats', params.location_id ?? ''],
    queryFn: () => purchaseOrdersApi.stats(params),
    staleTime: 15_000,
  });
}

export function usePurchaseOrder(id: string | undefined): UseQueryResult<PurchaseOrder | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['purchase-orders', tenantId, 'detail', id],
    queryFn: () => (id ? purchaseOrdersApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useCreatePO() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePOPayload) => purchaseOrdersApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
    },
  });
}

export function useUpdatePO() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreatePOPayload }) =>
      purchaseOrdersApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
    },
  });
}

export function useReceivePO() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ReceivePOPayload }) =>
      purchaseOrdersApi.receive(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId, 'detail', vars.id] });
    },
  });
}

/** POS comments item 10: amend an approved PO, decide a pending amendment, close lines short. */
export function useAmendPO() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AmendPOPayload }) => purchaseOrdersApi.amend(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId, 'detail', vars.id] });
    },
  });
}

export function useDecideAmendment() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, revisionId, approve, note }: { id: string; revisionId: string; approve: boolean; note?: string }) =>
      purchaseOrdersApi.decideAmendment(id, revisionId, approve, note),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId, 'detail', vars.id] });
    },
  });
}

export function useCloseRemaining() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, item_ids, reason }: { id: string; item_ids?: string[]; reason: string }) =>
      purchaseOrdersApi.closeRemaining(id, { item_ids, reason }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId, 'detail', vars.id] });
    },
  });
}

export function useApprovePO() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ApprovePOPayload }) =>
      purchaseOrdersApi.approve(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId, 'detail', vars.id] });
    },
  });
}

export function useRejectPO() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RejectPOPayload }) =>
      purchaseOrdersApi.reject(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId, 'detail', vars.id] });
    },
  });
}

export function useReverseReceipt() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, grnId, payload }: { id: string; grnId: string; payload: ReverseReceiptPayload }) =>
      purchaseOrdersApi.reverseReceipt(id, grnId, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId, 'detail', vars.id] });
    },
  });
}

export function useCancelPO() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CancelPOPayload }) =>
      purchaseOrdersApi.cancel(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId, 'detail', vars.id] });
    },
  });
}

/** Fetches the PO PDF blob; no cache invalidation (read-only). */
export function useDownloadPOPdf() {
  return useMutation({
    mutationFn: (id: string) => purchaseOrdersApi.downloadPdf(id),
  });
}

export function useNotifySupplier() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: NotifyPOPayload }) =>
      purchaseOrdersApi.notify(id, payload),
  });
}

export function useAutoGeneratePO() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AutoGeneratePOPayload) => purchaseOrdersApi.autoGenerate(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
    },
  });
}
